import { useState } from "react";
import { ONBOARDING_STAGES } from "@/data/onboardingData";
import { DataPointList } from "./DataPointList";
import { VisualDiagram } from "./VisualDiagram";
import { StepLayout } from "./StepLayout";
import { Zap, Hammer, Sun } from "lucide-react";
import type { OnboardingDataPoint } from "@/data/onboardingData";
import solarRooftopDiagram from "@/assets/solar-rooftop-diagram.png";
import solarElectricalDiagram from "@/assets/solar-electrical-diagram.png";
import solarStructuralDiagram from "@/assets/solar-structural-diagram.png";

interface DesignStepProps {
  selectedKeys: Set<string>;
  onToggle: (key: string) => void;
  onRequestMissing: () => void;
  activeView?: DesignView;
  onViewChange?: (view: DesignView) => void;
}

export type DesignView = "solar" | "electrical" | "structural";
export const DESIGN_VIEWS: DesignView[] = ["solar", "electrical", "structural"];
const VIEW_CONFIG: Record<DesignView, { label: string; icon: React.ElementType; diagramLabel: string }> = {
  solar: { label: "Solar Design", icon: Sun, diagramLabel: "Solar Design + Engineering" },
  electrical: { label: "Electrical", icon: Zap, diagramLabel: "Electrical Design" },
  structural: { label: "Structural", icon: Hammer, diagramLabel: "Structural Design" },
};

export function DesignStep({ selectedKeys, onToggle, onRequestMissing, activeView: activeViewProp, onViewChange }: DesignStepProps) {
  const stage = ONBOARDING_STAGES.find(s => s.id === "design")!;
  const [internalView, setInternalView] = useState<DesignView>("solar");
  const activeView = activeViewProp ?? internalView;
  const setActiveView = (v: DesignView) => { onViewChange ? onViewChange(v) : setInternalView(v); };

  const electricalKeys = new Set(stage.subsections?.find(s => s.label === "Electrical")?.keys || []);
  const structuralKeys = new Set(stage.subsections?.find(s => s.label === "Structural")?.keys || []);

  const solarPoints = stage.dataPoints.filter(dp => !electricalKeys.has(dp.key) && !structuralKeys.has(dp.key));
  const electricalPoints = stage.dataPoints.filter(dp => electricalKeys.has(dp.key));
  const structuralPoints = stage.dataPoints.filter(dp => structuralKeys.has(dp.key));

  const viewDataPoints: Record<DesignView, OnboardingDataPoint[]> = {
    solar: solarPoints,
    electrical: electricalPoints,
    structural: structuralPoints,
  };

  const currentPoints = viewDataPoints[activeView];
  const config = VIEW_CONFIG[activeView];

  return (
    <div className="space-y-4 pb-20">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold">{stage.heading}</h2>
          <p className="text-muted-foreground">{stage.subHeading}</p>
        </div>
      </div>

      <div className="flex gap-2">
        {(Object.keys(VIEW_CONFIG) as DesignView[]).map(view => {
          const vc = VIEW_CONFIG[view];
          const Icon = vc.icon;
          const isActive = activeView === view;
          const pts = viewDataPoints[view];
          const selectedCount = pts.filter(dp => selectedKeys.has(dp.key)).length;
          return (
            <button
              key={view}
              onClick={() => setActiveView(view)}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${
                isActive
                  ? "border-primary bg-primary/5 text-primary shadow-sm"
                  : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />
              {vc.label}
              <span className={`text-xs rounded-full px-1.5 py-0.5 ${isActive ? "bg-primary/10" : "bg-muted"}`}>
                {selectedCount}/{pts.length}
              </span>
            </button>
          );
        })}
      </div>

      <StepLayout
        dataCount={currentPoints.length}
        visual={
          <VisualDiagram
            diagramLabel={config.diagramLabel}
            dataPoints={currentPoints}
            selectedKeys={selectedKeys}
            onToggle={onToggle}
            imageSrc={
              activeView === "solar"
                ? solarRooftopDiagram
                : activeView === "electrical"
                ? solarElectricalDiagram
                : solarStructuralDiagram
            }
          />
        }
        list={
          <DataPointList
            dataPoints={currentPoints}
            selectedKeys={selectedKeys}
            onToggle={onToggle}
            onRequestMissing={onRequestMissing}
          />
        }
      />
    </div>
  );
}
